import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import AssignmentRounded from '@mui/icons-material/AssignmentRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import CloudOffRounded from '@mui/icons-material/CloudOffRounded';
import DoneAllRounded from '@mui/icons-material/DoneAllRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import NotificationsNoneRounded from '@mui/icons-material/NotificationsNoneRounded';
import NotificationsRounded from '@mui/icons-material/NotificationsRounded';
import PersonAddRounded from '@mui/icons-material/PersonAddRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import RocketLaunchRounded from '@mui/icons-material/RocketLaunchRounded';
import SyncProblemRounded from '@mui/icons-material/SyncProblemRounded';
import TaskAltRounded from '@mui/icons-material/TaskAltRounded';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import WavingHandRounded from '@mui/icons-material/WavingHandRounded';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Skeleton from '@mui/material/Skeleton';
import { alpha, type Theme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { NotificationResponse, NotificationType } from '@shared/schemas';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  groupNotifications,
  isActionRequired,
  notificationAction,
  type NotificationTab,
  notificationWhen,
} from '@/components/layout/notification-panel-logic';
import api from '@/shared/api/api-client';
import { logger } from '@/utils/logger';

const LIMIT = 10;

type Tone = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

const ICON_MAP: Record<NotificationType, React.ReactNode> = {
  welcome: <WavingHandRounded sx={{ fontSize: 16 }} />,
  new_list: <AssignmentRounded sx={{ fontSize: 16 }} />,
  invitation_accepted: <PersonAddRounded sx={{ fontSize: 16 }} />,
  cookie_expiry: <SyncProblemRounded sx={{ fontSize: 16 }} />,
  enrichment_started: <RocketLaunchRounded sx={{ fontSize: 16 }} />,
  enrichment_completed: <CheckCircleRounded sx={{ fontSize: 16 }} />,
  enrichment_failed: <ErrorOutlineRounded sx={{ fontSize: 16 }} />,
  list_feedback: <ChatBubbleOutlineRounded sx={{ fontSize: 16 }} />,
  feedback_reply: <ChatBubbleOutlineRounded sx={{ fontSize: 16 }} />,
  client_import: <UploadFileRounded sx={{ fontSize: 16 }} />,
};

const TONE_MAP: Record<NotificationType, Tone> = {
  welcome: 'neutral',
  new_list: 'primary',
  invitation_accepted: 'success',
  cookie_expiry: 'warning',
  enrichment_started: 'info',
  enrichment_completed: 'success',
  enrichment_failed: 'error',
  list_feedback: 'info',
  feedback_reply: 'primary',
  client_import: 'info',
};

/**
 * The bell in the top bar and the panel it opens.
 *
 * The panel opens on every notification, unread first by look rather than by
 * filter: opening on "Unread" showed an empty box as soon as everything had
 * been read, and a notification vanished the moment it was clicked. The Unread
 * tab is still there, with its count, for whoever wants only what is new.
 *
 * Unread is carried by four cues at once (tinted row, left bar, bold title,
 * dot) so it never rests on colour alone. Each row says where it leads (its
 * action verb) and the ones that block something say so in words.
 */
export default function NotificationDropdown() {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<NotificationResponse[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [tab, setTab] = useState<NotificationTab>('all');
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // ── Unread count (lightweight, for the badge and the Unread tab) ──
  const fetchUnreadCount = useCallback(() => {
    api
      .get<{ count: number }>('/notifications/unread-count')
      .then((d) => setUnreadCount(d.count))
      .catch((e: unknown) => logger.warn('Unread notification count failed', e));
  }, []);

  // Refresh on mount, on route changes, and when the browser tab comes back
  // into view; a notification raised while the user stays on one page would
  // otherwise wait for the next navigation to show up on the bell.
  useEffect(() => {
    fetchUnreadCount();
  }, [location.pathname, fetchUnreadCount]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchUnreadCount();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchUnreadCount]);

  // ── Notification list (paginated) ──
  const fetchNotifications = useCallback(async (filter: NotificationTab, cursor?: string) => {
    setLoading(true);
    setFailed(false);
    try {
      const params = new URLSearchParams({ filter, limit: String(LIMIT) });
      if (cursor) params.set('cursor', cursor);
      // The api client unwraps `data` and drops the root-level `has_more`, so
      // a full page is taken to mean there may be more.
      const res = await api.get<NotificationResponse[]>(`/notifications?${params}`);
      setNotifications((prev) => (cursor ? [...prev, ...res] : res));
      setHasMore(res.length === LIMIT);
    } catch (e) {
      logger.warn('Notification list failed', e);
      // A failed next page keeps what is already shown; a failed first page
      // says so instead of passing for an empty inbox.
      if (!cursor) setFailed(true);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Infinite scroll via IntersectionObserver ──
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loading) {
          const lastId = notifications[notifications.length - 1]?.id;
          if (lastId) fetchNotifications(tab, lastId);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, notifications, tab, fetchNotifications]);

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setNotifications([]);
    setTab('all');
    fetchNotifications('all');
    fetchUnreadCount();
  };
  const handleClose = () => setAnchorEl(null);

  // Reset and fetch when the tab changes
  const selectTab = (next: NotificationTab) => {
    if (next === tab) return;
    setTab(next);
    setNotifications([]);
    fetchNotifications(next);
  };

  const markRead = useCallback(
    (notif: NotificationResponse) => {
      if (notif.read) return;
      setNotifications((prev) => prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
      api
        .patch(`/notifications/${notif.id}/read`)
        .catch((e: unknown) => logger.warn('Mark notification read failed', e))
        .finally(fetchUnreadCount);
    },
    [fetchUnreadCount],
  );

  const handleClick = useCallback(
    (notif: NotificationResponse) => {
      markRead(notif);
      handleClose();
      if (notif.redirection_url) {
        navigate(notif.redirection_url);
      }
    },
    [navigate, markRead],
  );

  const handleMarkAllRead = () => {
    api
      .patch('/notifications/read-all')
      .then(() => {
        fetchUnreadCount();
        // Re-fetch to reflect the new read state (especially on the Unread tab)
        fetchNotifications(tab);
      })
      .catch((e: unknown) => logger.warn('Mark all notifications read failed', e));
  };

  const firstLoad = loading && notifications.length === 0;
  const groups = groupNotifications(notifications);

  return (
    <>
      <IconButton
        id="notification-bell"
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        onClick={handleOpen}
        size="small"
        sx={{
          color: 'text.secondary',
          transition: 'color 0.15s ease',
          '&:hover': { color: 'text.primary' },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="primary"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.625rem',
              fontWeight: 700,
              minWidth: 18,
              height: 18,
            },
          }}
        >
          <NotificationsRounded fontSize="small" />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 420,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: 560,
              borderRadius: 1.5,
              bgcolor: 'background.paper',
              border: (t) => `1px solid ${alpha(t.palette.divider, 0.4)}`,
              boxShadow: (t) => `0 12px 40px ${alpha(t.palette.common.black, 0.4)}`,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2.5,
            pt: 2,
            minHeight: 48,
            flexShrink: 0,
          }}
        >
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={handleMarkAllRead}
              startIcon={<DoneAllRounded sx={{ fontSize: 16 }} />}
              sx={{
                fontSize: '0.8125rem',
                textTransform: 'none',
                px: 1,
                color: (t) => t.palette.softInk.info,
              }}
            >
              Mark all as read
            </Button>
          )}
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v: NotificationTab) => selectTab(v)}
          aria-label="Notification filter"
          sx={{
            px: 2.5,
            minHeight: 40,
            flexShrink: 0,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 40,
              minWidth: 0,
              px: 0,
              mr: 2.5,
              textTransform: 'none',
              fontSize: '0.8125rem',
            },
          }}
        >
          <Tab value="all" label="All" />
          <Tab
            value="unread"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                Unread
                {unreadCount > 0 && (
                  <Box
                    component="span"
                    sx={{
                      px: 0.75,
                      minWidth: 20,
                      borderRadius: 2,
                      bgcolor: 'action.selected',
                      color: 'text.primary',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      lineHeight: '18px',
                    }}
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Box>
                )}
              </Box>
            }
          />
        </Tabs>

        {/* List */}
        <Box sx={{ overflowY: 'auto', flex: 1, pb: 1 }}>
          {firstLoad ? (
            <LoadingRows />
          ) : failed ? (
            <EmptyState
              icon={<CloudOffRounded sx={{ fontSize: 20 }} />}
              tone="error"
              title="Couldn't load notifications"
              text="Check your connection, then try again."
              action={
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<RefreshRounded sx={{ fontSize: 16 }} />}
                  onClick={() => fetchNotifications(tab)}
                  sx={{ textTransform: 'none' }}
                >
                  Retry
                </Button>
              }
            />
          ) : notifications.length === 0 && tab === 'unread' ? (
            <EmptyState
              icon={<TaskAltRounded sx={{ fontSize: 20 }} />}
              tone="success"
              title="You're all caught up"
              text="Nothing new since your last visit."
              action={
                <Button
                  size="small"
                  onClick={() => selectTab('all')}
                  sx={{ textTransform: 'none', color: (t) => t.palette.softInk.info }}
                >
                  Show all notifications
                </Button>
              }
            />
          ) : notifications.length === 0 ? (
            <EmptyState
              icon={<NotificationsNoneRounded sx={{ fontSize: 20 }} />}
              tone="neutral"
              title="No notifications yet"
              text="You'll be notified here when a list is shared with you or when something needs your attention."
            />
          ) : (
            groups.map((group) => (
              <Box key={group.label} component="section" aria-label={group.label}>
                <Typography
                  component="h3"
                  sx={{
                    px: 2.5,
                    pt: 1.75,
                    pb: 0.75,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {group.label}
                </Typography>
                {group.items.map((notif) => (
                  <NotificationRow
                    key={notif.id}
                    notif={notif}
                    onOpen={handleClick}
                    onMarkRead={markRead}
                  />
                ))}
              </Box>
            ))
          )}

          {/* Next page */}
          {loading && notifications.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {/* Infinite scroll sentinel */}
          {hasMore && !loading && <Box ref={sentinelRef} sx={{ height: 1 }} />}
        </Box>
      </Popover>
    </>
  );
}

/**
 * Text and icon colour for a tone. The brand swatches (`*.main`) are too pale to
 * write with on a light surface (teal on white is 1.6:1) so text goes through
 * `softInk`, the theme's per-mode inks measured for exactly this.
 */
function toneInk(t: Theme, tone: Tone): string {
  if (tone === 'neutral') return t.palette.text.secondary;
  return t.palette.softInk[tone === 'primary' ? 'info' : tone];
}

function ToneHalo({
  tone,
  size,
  children,
}: {
  tone: Tone;
  size: number;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: (t) =>
          alpha(tone === 'neutral' ? t.palette.text.secondary : t.palette[tone].main, 0.12),
        color: (t) => toneInk(t, tone),
      }}
    >
      {children}
    </Box>
  );
}

function NotificationRow({
  notif,
  onOpen,
  onMarkRead,
}: {
  notif: NotificationResponse;
  onOpen: (n: NotificationResponse) => void;
  onMarkRead: (n: NotificationResponse) => void;
}) {
  const tone = TONE_MAP[notif.type] ?? 'primary';
  const action = notif.redirection_url
    ? notificationAction(notif.type, notif.redirection_url)
    : null;
  const unread = !notif.read;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        borderLeft: (t) => `3px solid ${unread ? t.palette.softInk.info : 'transparent'}`,
        bgcolor: (t) => (unread ? alpha(t.palette.primary.main, 0.04) : 'transparent'),
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.08) },
        // The dot gives way to the "Mark as read" button on hover and while the
        // row has keyboard focus, so the button is reachable with Tab too.
        '& .notif-mark-read': { display: 'none' },
        '&:hover .notif-mark-read, &:focus-within .notif-mark-read': { display: 'inline-flex' },
        '&:hover .notif-unread-dot, &:focus-within .notif-unread-dot': { display: 'none' },
      }}
    >
      <ButtonBase
        onClick={() => onOpen(notif)}
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          gap: 1.5,
          pl: 2.25,
          pr: 0.5,
          py: 1.5,
          textAlign: 'left',
          borderRadius: 1,
          '&.Mui-focusVisible': {
            outline: (t) => `2px solid ${t.palette.focusRing}`,
            outlineOffset: -2,
          },
        }}
      >
        <Box sx={{ mt: 0.25 }}>
          <ToneHalo tone={tone} size={32}>
            {ICON_MAP[notif.type] ?? <NotificationsRounded sx={{ fontSize: 16 }} />}
          </ToneHalo>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isActionRequired(notif.type) && (
            <Chip
              size="small"
              variant="soft"
              color={tone === 'error' ? 'error' : 'warning'}
              icon={<WarningAmberRounded />}
              label="Action required"
              sx={{ mb: 0.5, height: 22, fontSize: '0.75rem', fontWeight: 600 }}
            />
          )}
          <Typography
            sx={{
              fontWeight: unread ? 600 : 500,
              lineHeight: 1.35,
              fontSize: '0.875rem',
              color: unread ? 'text.primary' : (t) => alpha(t.palette.text.primary, 0.85),
            }}
          >
            {notif.title}
          </Typography>
          <Typography
            sx={{
              color: 'text.secondary',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.45,
              fontSize: '0.8125rem',
              mt: 0.25,
            }}
          >
            {notif.content}
          </Typography>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, fontSize: '0.75rem' }}
          >
            {action && (
              <>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: (t) => t.palette.softInk.info,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                  }}
                >
                  {action}
                  <ArrowForwardRounded sx={{ fontSize: 14 }} />
                </Box>
                <Box component="span" aria-hidden sx={{ color: 'text.disabled' }}>
                  ·
                </Box>
              </>
            )}
            <Box component="time" dateTime={notif.created_at} sx={{ color: 'text.secondary' }}>
              {notificationWhen(notif.created_at)}
            </Box>
          </Box>
        </Box>
      </ButtonBase>

      {/* Trailing slot: a sibling of the row button, never nested in it */}
      <Box sx={{ width: 44, flexShrink: 0, display: 'flex', justifyContent: 'center', pt: 1.5 }}>
        {unread && (
          <>
            <Box
              className="notif-unread-dot"
              aria-hidden
              sx={{
                width: 8,
                height: 8,
                mt: 0.75,
                borderRadius: '50%',
                bgcolor: (t) => t.palette.softInk.info,
              }}
            />
            <Tooltip title="Mark as read">
              <IconButton
                className="notif-mark-read"
                size="small"
                aria-label={`Mark "${notif.title}" as read`}
                onClick={() => onMarkRead(notif)}
              >
                <CheckRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
}

function EmptyState({
  icon,
  tone,
  title,
  text,
  action,
}: {
  icon: React.ReactNode;
  tone: Tone;
  title: string;
  text: string;
  action?: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 1,
        px: 4,
        pt: 4.5,
        pb: 4,
      }}
    >
      <ToneHalo tone={tone} size={44}>
        {icon}
      </ToneHalo>
      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', mt: 0.5 }}>{title}</Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem', lineHeight: 1.45 }}>
        {text}
      </Typography>
      {action && <Box sx={{ mt: 1 }}>{action}</Box>}
    </Box>
  );
}

/** Placeholder rows shaped like the real ones, so the panel does not jump in height. */
function LoadingRows() {
  return (
    <Box aria-busy aria-label="Loading notifications">
      {[200, 160, 220].map((w) => (
        <Box key={w} sx={{ display: 'flex', gap: 1.5, px: 2.5, py: 1.75 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={w} sx={{ fontSize: '0.875rem' }} />
            <Skeleton variant="text" width="100%" sx={{ fontSize: '0.8125rem' }} />
            <Skeleton variant="text" width={80} sx={{ fontSize: '0.75rem' }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}
